import type { FC } from 'react';
import { useMemo } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import {
  ActivityIndicator,
  HelperText,
  RadioButton,
  Text
} from 'react-native-paper';

import type { EmailStatus } from '@/features/profile-setting/components/organization-creation/hooks/useEmailExistCheck';
import type {
  AdminTypeValue,
  CreateOrganizationForm
} from '@/features/profile-setting/schemas/createOrganization';
import { AdminType } from '@/features/profile-setting/schemas/createOrganization';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { FormTextInput } from '@/shared/components/forms/FormTextInput';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

export type { EmailStatus };

type Props = {
  control: Control<CreateOrganizationForm>;
  adminType: AdminTypeValue;
  emailStatus: EmailStatus;
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    description: {
      textAlign: 'center',
      paddingBottom: 8,
      fontWeight: 'bold'
    },
    stepHeader: { gap: 4 },
    radioItem: {
      paddingHorizontal: 0,
      paddingVertical: 4
    },
    helperText: {
      color: theme.colors.onSurfaceVariant,
      marginTop: -8
    },
    hint: {
      color: theme.colors.onSurfaceVariant
    },
    checkingRow: {
      marginTop: -8
    },
    bold: { fontWeight: 'bold' }
  });

export const OrganizationAdminStep: FC<Props> = ({
  control,
  adminType,
  emailStatus
}) => {
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-create'
  });
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  return (
    <>
      <Text style={styles.description} variant="titleMedium">
        {t('steps.adminSubtitle')}
      </Text>
      <View style={styles.stepHeader}>
        <Text variant="titleMedium">{t('steps.whoIsAdmin')}</Text>
      </View>
      <Controller
        control={control}
        name="adminType"
        render={({ field: { onChange, value } }) => (
          <RadioButton.Group onValueChange={(v) => onChange(v)} value={value}>
            <RadioButton.Item
              label={t('steps.selfOption')}
              value={AdminType.SELF}
              style={styles.radioItem}
              labelVariant="bodyLarge"
            />
            <RadioButton.Item
              label={t('steps.otherPersonOption')}
              value={AdminType.OTHER}
              style={styles.radioItem}
              labelVariant="bodyLarge"
            />
          </RadioButton.Group>
        )}
      />
      <Text variant="bodySmall" style={styles.helperText}>
        {t('steps.emailHelp')}
      </Text>
      {adminType === AdminType.OTHER && (
        <>
          <FormTextInput
            control={control}
            name="adminEmail"
            mode="outlined"
            keyboardType="email-address"
            autoCapitalize="none"
            label={t('fields.adminEmail')}
            placeholder={t('placeholders.adminEmail')}
          />
          {emailStatus === 'checking' && (
            <HelperText type="info" style={styles.checkingRow}>
              <ActivityIndicator size={12} />
              {'  '}
              {t('steps.emailChecking')}
            </HelperText>
          )}
          <Text variant="bodySmall" style={styles.hint}>
            {t('steps.registeredUsersHintPrefix')}
            <Text style={styles.bold}>
              {t('steps.registeredUsersHintBold')}
            </Text>
            {t('steps.registeredUsersHintSuffix')}
          </Text>
        </>
      )}
    </>
  );
};
